/**
 * Presentation helpers for the Sync tab — shared by the review grid and the
 * auto-approval decision view so the two can never say different things about
 * the same number.
 *
 * The money and profit helpers were lifted out of `PartnerSync/index.jsx`
 * unchanged. They are here rather than there because the decision view shows
 * the SAME quantities (our price, the partner's offer, the margin between
 * them) and a second copy of the thresholds is a second copy that can drift.
 */

// Profit thresholds for the "our price" colour cue on each sync card.
// Computed at ingest time in the backend (ISyncProfitAnalysis.profit_pct =
// (partner_offer − our_price) / our_price). Green = ≥10%, yellow = 5–10%,
// red = anything below (including negative). If the backend couldn't
// resolve pricing the card carries no analysis and we skip the cue.
//
// `features.margin` on a decision row is the same kind of quantity on the
// same scale, so it is toned with the same thresholds.
export const PROFIT_GREEN_THRESHOLD = 0.10;
export const PROFIT_YELLOW_THRESHOLD = 0.05;

/**
 * "WE DO NOT HAVE THIS NUMBER" — the one predicate, for every helper below.
 *
 * `Number(null)` and `Number('')` are both 0, so a guard built on
 * `Number.isFinite` alone turns an absent price into a confident 0.00, an
 * absent margin into a confident 0.0% and an unknown lead time into "0
 * days". On this screen a confident zero is a price decision. Absence is
 * therefore checked BEFORE any coercion, here, once — a decision row's
 * `features` carries an explicit `null` for every fact the policy could not
 * measure, which is most of them on an offer we could not price.
 */
export const isAbsent = (value) => value === null || value === undefined || value === '';

export const profitToneFor = (profitPct) => {
  // `isAbsent` first: a margin nobody measured is MUTED, not red. Coercing
  // it would tone an unpriceable offer as a loss — beside a margin column
  // that correctly shows a dash.
  if (isAbsent(profitPct) || !Number.isFinite(Number(profitPct))) return 'muted';
  const value = Number(profitPct);
  if (value >= PROFIT_GREEN_THRESHOLD) return 'good';
  if (value >= PROFIT_YELLOW_THRESHOLD) return 'warn';
  return 'bad';
};

const round2 = (amount) => (Math.round(Number(amount) * 100) / 100).toFixed(2);

export const formatMoney = (amount, currency) => `${round2(amount)} ${currency}`;

/**
 * Money that may legitimately be absent. A decision row's `features` can
 * carry a null price (pricing did not resolve), and "NaN undefined" on a
 * screen somebody is about to accept a booking on is worse than a dash.
 */
export const formatMoneyOrDash = (amount, currency) => {
  if (isAbsent(amount) || !Number.isFinite(Number(amount))) return '—';
  return currency ? formatMoney(amount, currency) : round2(amount);
};

/**
 * THE ONE PLACE A MARGIN IS MULTIPLIED BY 100.
 *
 * `features.margin` and `ProfitAnalysis.profit_pct` are both FRACTIONS, not
 * percentages — production runs from −0.905 to +1.808. Rendering 0.185 as
 * "0.19%" or 18.5 as "1850%" are both one keystroke away, and both would be
 * read as a price decision. Every margin on this tab goes through here so
 * there is exactly one multiplication to get wrong, and it is pinned by a
 * test.
 */
export const formatFractionAsPct = (fraction) => {
  if (isAbsent(fraction)) return '—';
  const value = Number(fraction);
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * A number we actually have, formatted — or a dash.
 *
 * The decision log shows four of these (lead time, pickup hour, passengers, a
 * step's duration) and each one can be absent, so the guard lives here rather
 * than once per JSX line: `Number.isFinite(Number(x))` written inline reads
 * an unknown lead time as "0 days" and an unknown pickup as "00:00 local",
 * which are measurements nobody took.
 */
export const formatNumberOrDash = (value, format = String) => {
  if (isAbsent(value) || !Number.isFinite(Number(value))) return '—';
  return format(Number(value));
};

// The margin's own name for the one multiplication above. Every margin on the
// tab — the review grid's profit cue and the decision log's margin column —
// is rendered through this, so there is no second scale to keep in step.
export const formatMarginPct = formatFractionAsPct;

/**
 * Turn a backend token (`AUTO_APPROVED`, `demand_unknown`) into something
 * readable, so an enum value we have never seen renders as words instead of
 * as shouting or as a blank.
 */
export const prettifyToken = (token, fallback = 'Unknown') => (token || fallback)
  .toString()
  .toLowerCase()
  .replace(/_/g, ' ')
  .replace(/^\w/, (c) => c.toUpperCase());
